// Cấu hình lịch làm việc đọc từ tblClientInfo của LOGI (LOGI-data-dictionary.md · "tblClientInfo — lịch làm việc"):
//   WorkDays "2,3,4,5,6,7" (2 = thứ Hai … 7 = thứ Bảy, 8 / 1 = Chủ nhật) · WorkMorningStart/End · WorkAfternoonStart/End ·
//   WorkSatEnd (thứ Bảy làm một ca từ đầu ca sáng tới giờ này) · AnnualLeaveDays ·
//   Holidays: mỗi dòng "YYYY-MM-DD[..YYYY-MM-DD] Tên lễ" (cờ #cty = nghỉ riêng công ty) ·
//   WorkdayOverrides: mỗi dòng "YYYY-MM-DD Ghi chú" = ngày làm bù (làm đủ ca sáng + chiều).

/** Phút trong ngày (0–1440). */
export type Minute = number;

export interface Holiday {
  from: string;
  to: string;
  name: string;
  /** nghỉ riêng công ty (#cty) */
  company: boolean;
}

export interface WorkConfig {
  /** thứ trong tuần được làm, theo quy ước JS 0 = CN … 6 = T7 */
  weekdays: ReadonlySet<number>;
  morning: readonly [Minute, Minute];
  afternoon: readonly [Minute, Minute];
  /** giờ kết thúc thứ Bảy; `null` = thứ Bảy làm như ngày thường */
  satEnd: Minute | null;
  holidays: readonly Holiday[];
  /** ngày làm bù YYYY-MM-DD */
  overrides: ReadonlySet<string>;
  annualLeaveDays: number;
}

/** Một dòng tblClientInfo (chỉ các cột lịch). Giá trị để dạng chuỗi như DB. */
export interface ClientInfoRow {
  WorkDays?: string | null;
  WorkMorningStart?: string | null;
  WorkMorningEnd?: string | null;
  WorkAfternoonStart?: string | null;
  WorkAfternoonEnd?: string | null;
  WorkSatEnd?: string | null;
  AnnualLeaveDays?: number | string | null;
  Holidays?: string | null;
  WorkdayOverrides?: string | null;
}

const HHMM = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
export function parseHHMM(s: string): Minute {
  const m = HHMM.exec(s.trim());
  if (!m || Number(m[1]) > 24 || Number(m[2]) > 59) throw new Error(`Giờ không hợp lệ: ${s}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

const COMMENT = /^(#|\/\/)/;
const lines = (s: string | null | undefined) =>
  (s ?? '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !COMMENT.test(l));

const DATE = String.raw`(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}/\d{1,2}/\d{4})`;
const RANGE_SEP = String.raw`(?:\.\.|--|–|—|~|→)`;
const DAY_LINE = new RegExp(String.raw`^${DATE}(?:\s*${RANGE_SEP}\s*${DATE})?\s*(.*)$`);

/** "YYYY-MM-DD" hoặc "dd/mm/yyyy" → "YYYY-MM-DD"; ngày không có thật → null. */
function normDate(s: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  const vn = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  const [y, mo, d] = iso ? [iso[1], iso[2], iso[3]] : vn ? [vn[3], vn[2], vn[1]] : [];
  if (!y) return null;
  const t = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  if (t.getUTCFullYear() !== Number(y) || t.getUTCMonth() !== Number(mo) - 1 || t.getUTCDate() !== Number(d)) return null;
  return t.toISOString().slice(0, 10);
}

/** Mỗi dòng "ngày[ khoảng] ghi chú" (theo parseDayLines của LOGI): nhận YYYY-MM-DD và dd/mm/yyyy, ngăn khoảng `..` `--` `–` `~` `→`,
 *  bỏ dòng `#` / `//`, khoảng ngược tự đảo, dòng hỏng thì bỏ qua (không làm hỏng cả lịch). */
export function parseDayLines(text: string | null | undefined): Array<{ from: string; to: string; rest: string }> {
  const out: Array<{ from: string; to: string; rest: string }> = [];
  for (const l of lines(text)) {
    const m = DAY_LINE.exec(l);
    if (!m) continue;
    const a = normDate(m[1]!);
    const b = m[2] ? normDate(m[2]) : a;
    if (!a || !b) continue;
    out.push({ from: a < b ? a : b, to: a < b ? b : a, rest: (m[3] ?? '').trim() });
  }
  return out;
}

export function parseHolidays(text: string | null | undefined): Holiday[] {
  return parseDayLines(text).map(({ from, to, rest }) => ({
    from,
    to,
    name: rest.replace(/#cty/gi, '').trim(),
    company: /#cty/i.test(rest),
  }));
}

/** Ngày làm bù; nhận cả khoảng ngày → trả về từng ngày. */
export function parseOverrides(text: string | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const { from, to } of parseDayLines(text)) {
    for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += 86_400_000) out.add(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

const strip = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .trim();

/** "2,3,4,5,6,7" → {1,…,6} (JS weekday). Nhận số 1–7 (1 = Chủ nhật), `CN`, `Chủ nhật`, `T2`, `Thứ 2` (theo client-info.js của LOGI).
 *  `8` và mục không đọc được là lỗi nhập → bỏ; không còn ngày nào thì báo lỗi. */
export function parseWorkDays(s: string): Set<number> {
  const out = new Set<number>();
  for (const raw of s.split(/[,;]+/)) {
    const p = strip(raw);
    if (p === 'cn' || p === 'chu nhat' || p === '1') out.add(0);
    else {
      const m = /^(?:t|thu\s*)?([2-7])$/.exec(p);
      if (m) out.add(Number(m[1]) - 1);
    }
  }
  if (!out.size) throw new Error(`WorkDays không hợp lệ: ${s}`);
  return out;
}

/** Đọc dòng tblClientInfo. Cột trống → báo lỗi (không tự đoán giờ làm việc). */
export function configFromClientInfo(row: ClientInfoRow): WorkConfig {
  const need = (k: keyof ClientInfoRow): string => {
    const v = row[k];
    if (v === null || v === undefined || String(v).trim() === '') throw new Error(`tblClientInfo thiếu ${k}`);
    return String(v);
  };
  const morning = [parseHHMM(need('WorkMorningStart')), parseHHMM(need('WorkMorningEnd'))] as const;
  const afternoon = [parseHHMM(need('WorkAfternoonStart')), parseHHMM(need('WorkAfternoonEnd'))] as const;
  if (!(morning[0] < morning[1] && morning[1] <= afternoon[0] && afternoon[0] < afternoon[1])) throw new Error('Ca sáng / chiều chồng nhau');
  const sat = row.WorkSatEnd?.trim();
  return {
    weekdays: parseWorkDays(need('WorkDays')),
    morning,
    afternoon,
    satEnd: sat ? parseHHMM(sat) : null,
    holidays: parseHolidays(row.Holidays),
    overrides: parseOverrides(row.WorkdayOverrides),
    annualLeaveDays: Number(row.AnnualLeaveDays ?? 0) || 0,
  };
}
